import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { signInWithGoogle, logOut, auth, onAuthStateChanged } from '../firebase';
import talentsData from '../data/talents.json';
import servicesData from '../data/services.json';
import testimonialsData from '../data/testimonials.json';
import partnersData from '../data/partners.json';

// List of authorized admin emails (from env or empty array)
const ADMIN_EMAILS = process.env.REACT_APP_ADMIN_EMAILS?.split(',').map(e => e.trim().toLowerCase()) || [];

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('talents');
  const [talents, setTalents] = useState(talentsData);
  const [services, setServices] = useState(servicesData);
  const [testimonials, setTestimonials] = useState(testimonialsData);
  const [partners, setPartners] = useState(partnersData);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setAuthError('');
      const result = await signInWithGoogle();
      const email = result.user.email.toLowerCase();
      
      // Check if email is authorized
      if (ADMIN_EMAILS.length > 0 && !ADMIN_EMAILS.includes(email)) {
        await logOut();
        setAuthError(`Access denied. ${email} is not authorized. Contact the administrator to add your email.`);
      }
    } catch (error) {
      setAuthError('Sign in failed. Please try again.');
      console.error('Auth error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAuthorized = user && (ADMIN_EMAILS.length === 0 || ADMIN_EMAILS.includes(user.email.toLowerCase()));

  const handleEdit = (item, type, index) => {
    setEditing({ type, index });
    setFormData({ ...item });
  };

  const handleSave = () => {
    if (!editing) return;
    
    const { type, index } = editing;
    if (type === 'talents') {
      const updated = [...talents];
      updated[index] = formData;
      setTalents(updated);
    } else if (type === 'services') {
      const updated = [...services];
      updated[index] = formData;
      setServices(updated);
    } else if (type === 'testimonials') {
      const updated = [...testimonials];
      updated[index] = formData;
      setTestimonials(updated);
    } else if (type === 'partners') {
      const updated = [...partners];
      updated[index] = formData;
      setPartners(updated);
    }
    
    setEditing(null);
    setFormData({});
  };

  const handleDelete = (type, index) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    if (type === 'talents') setTalents(talents.filter((_, i) => i !== index));
    else if (type === 'services') setServices(services.filter((_, i) => i !== index));
    else if (type === 'testimonials') setTestimonials(testimonials.filter((_, i) => i !== index));
    else if (type === 'partners') setPartners(partners.filter((_, i) => i !== index));
  };

  const handleAdd = (type) => {
    const newItem = type === 'talents' 
      ? { id: 'new', name: 'New Talent', reach: '0', category: '', image: '', socials: {}, featured: false }
      : type === 'services'
      ? { id: 'new', icon: 'FaGamepad', title: 'New Service', subtitle: '', description: '' }
      : type === 'testimonials'
      ? { id: 'new', name: '', role: '', quote: '', image: '' }
      : { name: 'New Partner', logo: '' };
    
    if (type === 'talents') setTalents([...talents, newItem]);
    else if (type === 'services') setServices([...services, newItem]);
    else if (type === 'testimonials') setTestimonials([...testimonials, newItem]);
    else if (type === 'partners') setPartners([...partners, newItem]);
  };

  const exportData = () => {
    const data = {
      talents,
      services,
      testimonials,
      partners,
      exportDate: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'thirdrail-content-export.json';
    a.click();
  };

  const renderForm = () => {
    if (!editing) return null;
    
    return (
      <div className="admin-modal-overlay" onClick={() => setEditing(null)}>
        <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
          <h3>Edit {editing.type.slice(0, -1)}</h3>
          <div className="admin-form">
            {Object.entries(formData).map(([key, value]) => (
              <div key={key} className="form-field">
                <label>{key}</label>
                {typeof value === 'string' ? (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                  />
                ) : (
                  <textarea
                    value={JSON.stringify(value, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, [key]: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    rows={4}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="admin-modal-actions">
            <button className="admin-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="admin-btn-primary" onClick={handleSave}>Save Changes</button>
          </div>
        </div>
      </div>
    );
  };

  const renderList = (items, type) => (
    <div className="admin-list">
      {items.map((item, index) => (
        <div key={index} className="admin-list-item">
          <div className="admin-item-preview">
            {item.image && (
              <img src={process.env.PUBLIC_URL + item.image} alt={item.name || item.title} />
            )}
            <div className="admin-item-info">
              <h4>{item.name || item.title || item.id}</h4>
              <p>{item.category || item.subtitle || item.role || item.name}</p>
            </div>
          </div>
          <div className="admin-item-actions">
            <button className="admin-btn-edit" onClick={() => handleEdit(item, type, index)}>Edit</button>
            <button className="admin-btn-delete" onClick={() => handleDelete(type, index)}>Delete</button>
          </div>
        </div>
      ))}
      <button className="admin-btn-add" onClick={() => handleAdd(type)}>
        + Add New {type.slice(0, -1)}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-box">
          <div className="admin-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-box">
          <img src={process.env.PUBLIC_URL + '/Image16.png'} alt="ThirdRail Avenue" className="admin-login-logo" />
          <h2>Admin Access</h2>
          
          {authError && (
            <div className="admin-config-warning">
              <strong>⚠️ Access Denied</strong>
              <p>{authError}</p>
            </div>
          )}
          
          {!authError && ADMIN_EMAILS.length === 0 && (
            <div className="admin-config-warning">
              <strong>⚠️ Not Configured</strong>
              <p>Set REACT_APP_ADMIN_EMAILS in your .env file</p>
              <p>See .env.example for details</p>
            </div>
          )}
          
          <button onClick={handleGoogleSignIn} className="google-signin-btn">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
          
          <Link to="/" className="admin-login-back">← Back to Website</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="admin-nav">
        <Link to="/" className="admin-logo">
          <img src={process.env.PUBLIC_URL + '/Image16.png'} alt="ThirdRail Avenue" />
        </Link>
        <h1>Content Admin</h1>
        <div className="admin-nav-actions">
          <div className="admin-user">
            {user.photoURL && <img src={user.photoURL} alt="" className="admin-user-avatar" />}
            <span className="admin-user-email">{user.email}</span>
          </div>
          <button className="admin-btn-export" onClick={exportData}>Export JSON</button>
          <button className="admin-btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </nav>

      <div className="admin-container">
        <div className="admin-tabs">
          {['talents', 'services', 'testimonials', 'partners'].map(tab => (
            <button
              key={tab}
              className={activeTab === tab ? 'active' : ''}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="admin-content">
          <div className="admin-instructions">
            <p><strong>Note:</strong> Changes are preview only. To apply changes, export the JSON and update the files in <code>src/data/</code>.</p>
          </div>
          
          {activeTab === 'talents' && renderList(talents, 'talents')}
          {activeTab === 'services' && renderList(services, 'services')}
          {activeTab === 'testimonials' && renderList(testimonials, 'testimonials')}
          {activeTab === 'partners' && renderList(partners, 'partners')}
        </div>
      </div>

      {renderForm()}
    </div>
  );
};

export default AdminDashboard;
